import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../components/layout/AuthLayout";
import Button from "../components/common/Button";
import OtpInputs from "../components/common/OtpInputs";
import PasswordStrengthChecker from "../components/common/PasswordStrengthChecker";
import {
  requestAgentPasswordReset,
  resetAgentPassword,
  verifyAgentPasswordResetOtp,
} from "../lib/agentAuth";
import { formatOtpCountdown } from "../lib/agentOnboarding";
import { persistSuccessAction, showApiError, showError, showSuccessAction } from "../lib/alerts";
import { getApiErrorMessage } from "../lib/apiMessages";
import { BRAND } from "../lib/brand";
import { evaluatePassword } from "../lib/passwordStrength";

const STEP_EMAIL = "email";
const STEP_OTP = "otp";
const STEP_PASSWORD = "password";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [otpSeconds, setOtpSeconds] = useState(0);
  const [otpError, setOtpError] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const canResendOtp = otpSeconds <= 0;
  const passwordStrength = evaluatePassword(newPassword);
  const passwordsMatch = Boolean(confirmPassword) && newPassword === confirmPassword;

  useEffect(() => {
    if (otpSeconds <= 0) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setOtpSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpSeconds]);

  async function sendOtp({ resend = false } = {}) {
    setLoading(true);
    setOtpError("");
    setEmailError("");
    try {
      const result = await requestAgentPasswordReset(email.trim(), { otpToken });
      setOtpToken(result.otpToken || "");
      setOtpSeconds(Number(result.expiresIn || result.resendAvailableIn || 120));
      setOtpValue("");
      showSuccessAction(resend ? "RESEND_OTP" : "FORGOT_PASSWORD");
      setStep(STEP_OTP);
      return true;
    } catch (error) {
      const remaining = Number(error?.payload?.data?.remainingSeconds || error?.payload?.remainingSeconds || 0);
      const message = getApiErrorMessage(error, "FORGOT_PASSWORD");
      if (remaining > 0) {
        setOtpSeconds(remaining);
        setStep(STEP_OTP);
        setOtpError(message);
      } else {
        setEmailError(message);
      }
      showApiError(error, "FORGOT_PASSWORD");
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailSubmit(event) {
    event.preventDefault();
    await sendOtp();
  }

  async function handleVerifyOtp(event) {
    event.preventDefault();
    setLoading(true);
    setOtpError("");
    try {
      const result = await verifyAgentPasswordResetOtp({
        email: email.trim(),
        otp: otpValue.trim(),
        otpToken,
      });
      setResetToken(result.resetToken || "");
      showSuccessAction("VERIFY_OTP");
      setStep(STEP_PASSWORD);
    } catch (error) {
      const message = getApiErrorMessage(error, "VERIFY_OTP");
      setOtpError(message);
      showApiError(error, "VERIFY_OTP");
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(event) {
    event.preventDefault();
    if (!passwordStrength.passed) {
      showError(passwordStrength.message);
      return;
    }
    if (!passwordsMatch) {
      showError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await resetAgentPassword({
        resetToken,
        newPassword,
        confirmPassword,
      });
      persistSuccessAction("RESET_PASSWORD");
      navigate("/login", { replace: true });
    } catch (error) {
      showApiError(error, "RESET_PASSWORD");
    } finally {
      setLoading(false);
    }
  }

  function goBackToEmail() {
    setStep(STEP_EMAIL);
    setOtpValue("");
    setOtpError("");
    setEmailError("");
  }

  return (
    <AuthLayout
      heroImage="/template/assets/images/auth/forgotpasswordimage.png"
      heroAlt="Forgot Password?"
    >
      
      <div className="max-w-464-px mx-auto w-100 auth-form-card">
        <Link to="/login" className="mb-40 max-w-290-px d-inline-block">
          <img src={BRAND.logo} alt={BRAND.name} className="auth-brand-logo" />
        </Link>

        {step === STEP_EMAIL ? (
          <>
            <h4 className="mb-12">Forgot password</h4>
            <p className="mb-24 text-secondary-light text-lg">
              Enter your registered Agent Portal email. We’ll send a 6-digit OTP only if the account exists.
            </p>
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-16">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="forgot-email">
                  Email
                </label>
                <input
                  type="email"
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  id="forgot-email"
                  placeholder="name@agency.com"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setEmailError("");
                    setOtpToken("");
                    setResetToken("");
                  }}
                  required
                />
                <small className={`text-danger-600 text-xs mt-8 ${emailError ? "" : "d-none"}`}>{emailError}</small>
              </div>
              <Button type="submit" className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12" disabled={loading}>
                {loading ? "Sending OTP..." : "Send OTP"}
              </Button>
              <div className="mt-24 text-center text-sm">
                <p className="mb-0">
                  Remembered your password? <Link to="/login" className="text-primary-600 fw-semibold">Back to sign in</Link>
                </p>
              </div>
            </form>
          </>
        ) : null}

        {step === STEP_OTP ? (
          <>
            <h4 className="mb-12">Verify OTP</h4>
            <p className="mb-24 text-secondary-light text-lg">
              Enter the 6-digit OTP sent to <strong>{email || "your email"}</strong>. It expires in 2 minutes.
            </p>
            <form onSubmit={handleVerifyOtp}>
              <OtpInputs idPrefix="reset-otp" value={otpValue} onChange={setOtpValue} disabled={loading} />
              <small className={`text-danger-600 text-xs mt-8 ${otpError ? "" : "d-none"}`}>{otpError}</small>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-8 mt-16 mb-16">
                <div className="text-secondary-light text-sm">
                  {canResendOtp ? "You can resend the OTP now." : `Resend OTP in ${formatOtpCountdown(otpSeconds)}`}
                </div>
                <Button
                  type="button"
                  className="btn btn-outline-secondary border-neutral-100 px-24 radius-12"
                  disabled={!canResendOtp || loading}
                  onClick={() => sendOtp({ resend: true })}
                >
                  {loading ? "Please wait..." : "Resend OTP"}
                </Button>
              </div>
              <Button type="submit" className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12" disabled={loading || otpValue.length !== 6}>
                {loading ? "Verifying..." : "Verify OTP"}
              </Button>
              <div className="mt-24 text-center text-sm">
                <p className="mb-0">
                  <button type="button" className="btn btn-link p-0 text-primary-600 fw-semibold" onClick={goBackToEmail}>
                    Use a different email
                  </button>
                </p>
              </div>
            </form>
          </>
        ) : null}

        {step === STEP_PASSWORD ? (
          <>
            <h4 className="mb-12">Set a new password</h4>
            <p className="mb-24 text-secondary-light text-lg">
              Email verified. Choose a new password for {email}.
            </p>
            <form onSubmit={handleResetPassword}>
              <div className="mb-16">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="new-password">
                  New password
                </label>
                <input
                  type="password"
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  id="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <PasswordStrengthChecker password={newPassword} />
              </div>
              <div className="mb-16">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="confirm-password">
                  Confirm password
                </label>
                <input
                  type="password"
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <small className={`text-xs mt-8 ${confirmPassword ? "" : "d-none"} ${passwordsMatch ? "text-success-600" : "text-danger-600"}`}>
                  {passwordsMatch ? "Passwords match." : "Passwords do not match."}
                </small>
              </div>
              <Button
                type="submit"
                className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12"
                disabled={loading || !passwordStrength.passed || !passwordsMatch}
              >
                {loading ? "Resetting..." : "Reset password"}
              </Button>
              <div className="mt-24 text-center text-sm">
                <p className="mb-0">
                  Back to <Link to="/login" className="text-primary-600 fw-semibold">sign in</Link>
                </p>
              </div>
            </form>
          </>
        ) : null}
      </div>
    </AuthLayout>
  );
}
